import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AI_TOOLS,
  AI_APPS,
  TEAM_PROFICIENCIES,
  WORKFLOW_STEPS,
  SUB_AGENT_NAMES,
  BUSINESS_BENEFITS,
  PRIVATE_BRAND_ENVS,
  PLATFORMS,
  STATS,
  SKILL_LOOP,
  BRANDS,
} from "@/data/growthOSData";
import Brain3D from "@/components/Brain3D";
import BrandIntelligenceCore from "@/components/BrandIntelligenceCore";
import {
  Cpu,
  Server,
  HardDrive,
  Lock,
  Sparkles,
  ChevronRight,
  AlertTriangle,
  Workflow,
  Target,
  Check,
  Quote,
  Shield,
  KeyRound,
  X,
  ZoomIn,
  ExternalLink,
  BarChart3,
  Users,
  Brain,
  TrendingDown,
  TrendingUp,
  Package,
  Video,
  MessageSquare,
  LayoutDashboard,
  Unlink,
  Database,
  Globe,
} from "lucide-react";

/* ───────── Shared eyebrow + title block ───────── */
function SlideHeader({ eyebrow, title, large = false }) {
  return (
    <div className="text-center mb-3 sm:mb-4 max-w-3xl mx-auto">
      <div className="text-[10px] sm:text-[11px] tracking-[0.42em] text-white/45 font-mono uppercase mb-1.5">
        {eyebrow}
      </div>
      <h2
        className={`font-heading font-light tracking-tight text-white ${
          large ? "text-3xl sm:text-5xl lg:text-6xl" : "text-2xl sm:text-3xl lg:text-4xl"
        }`}
      >
        {title}
      </h2>
    </div>
  );
}

function Footnote({ text }) {
  return (
    <p className="mt-4 sm:mt-5 text-center text-white/55 italic text-xs sm:text-sm max-w-2xl mx-auto px-4">
      {text}
    </p>
  );
}

/* ───────── 1) TITLE SLIDE ───────── */
export function TitleSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6 relative">
      <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
        <div className="w-[min(70vh,720px)] aspect-square">
          <Brain3D paused={false} />
        </div>
      </div>
      <div className="relative z-10 text-center">
        <div className="text-[11px] sm:text-[12px] tracking-[0.5em] text-white/50 font-mono uppercase mb-4 sm:mb-6">
          {slide.eyebrow}
        </div>
        <h1 className="shimmer font-heading text-5xl sm:text-7xl lg:text-8xl font-light tracking-tighter mb-4 sm:mb-6">
          {slide.title}
        </h1>
        <p className="font-heading text-base sm:text-2xl text-white/75 tracking-wide max-w-2xl mx-auto font-light">
          {slide.subtitle}
        </p>
      </div>
    </div>
  );
}

/* ───────── 2/3) CHIP GRID (AI tools & apps) ───────── */
export function ChipGridSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3 max-w-4xl mt-2">
        {slide.items.map((it, i) => (
          <div
            key={it.id}
            className="glass-strong rounded-xl px-3.5 py-2.5 flex flex-col items-start gap-0.5"
            style={{
              borderTop: "1px solid rgba(70,144,217,0.4)",
              boxShadow: "0 0 18px rgba(70,144,217,0.18)",
              animation: `node-in 0.5s ${i * 0.06}s both`,
            }}
            data-testid={`chip-${it.id}`}
          >
            <div className="font-heading text-base sm:text-lg font-medium text-white tracking-wide">
              {it.name}
            </div>
            <div className="text-[9px] sm:text-[10px] tracking-[0.18em] uppercase font-mono text-white/45">
              {it.note}
            </div>
          </div>
        ))}
      </div>
      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

/* ───────── 4) FRAGMENTATION ───────── */
export function FragmentationSlide({ slide }) {
  const blocks = [
    { label: "Tools", color: "#4690D9" },
    { label: "Apps", color: "#9CC6F0" },
    { label: "Shopify", color: "#34D399" },
    { label: "Klaviyo", color: "#F59E0B" },
    { label: "Meta Ads", color: "#0866FF" },
    { label: "Google Ads", color: "#4285F4" },
    { label: "Team Skill A", color: "#E11D48" },
    { label: "Team Skill B", color: "#FBBF24" },
  ];
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="relative w-full max-w-3xl aspect-[16/8] flex items-center justify-center">
        {blocks.map((b, i) => {
          const angle = (i / blocks.length) * Math.PI * 2;
          const r = 38;
          const x = 50 + Math.cos(angle) * r;
          const y = 50 + Math.sin(angle) * (r * 0.55);
          return (
            <div
              key={b.label}
              className="absolute -translate-x-1/2 -translate-y-1/2 glass rounded-lg px-2.5 py-1.5"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                borderLeft: `2px solid ${b.color}`,
                animation: `node-in 0.5s ${i * 0.07}s both, soft-glow 3.${i}s ease-in-out infinite`,
              }}
            >
              <div className="text-[10px] sm:text-xs tracking-[0.18em] uppercase font-mono text-white/80">
                {b.label}
              </div>
            </div>
          );
        })}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="glass-strong rounded-full p-4 sm:p-5" style={{ boxShadow: "0 0 28px rgba(225,29,72,0.4)" }}>
            <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-rose-300" strokeWidth={1.6} />
          </div>
        </div>
      </div>
      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

/* ───────── 5) OPERATOR BOTTLENECK ───────── */
/* ───────── 4) SILOS (AI tools fragment teams) ───────── */
export function SilosSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 sm:gap-4 max-w-3xl w-full mt-1">
        {/* Silo 1 */}
        <SiloTower silo={slide.silos[0]} index={0} align="left" />

        {/* Broken link */}
        <div className="flex flex-col items-center justify-center gap-2 px-1 sm:px-2" data-testid="silo-broken-link">
          <div className="w-px flex-1 border-l border-dashed border-rose-400/30" />
          <div
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center shrink-0"
            style={{ border: "1px solid rgba(225,29,72,0.45)", boxShadow: "0 0 18px rgba(225,29,72,0.25)" }}
          >
            <Unlink className="w-5 h-5 text-rose-300" strokeWidth={1.8} />
          </div>
          <span className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.2em] text-rose-300/80 text-center leading-tight">
            No shared<br />context
          </span>
          <div className="w-px flex-1 border-l border-dashed border-rose-400/30" />
        </div>

        {/* Silo 2 */}
        <SiloTower silo={slide.silos[1]} index={1} align="right" />
      </div>
      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

function SiloTower({ silo, index }) {
  return (
    <div
      className="glass-strong rounded-2xl p-3 sm:p-4 flex flex-col gap-2"
      style={{
        borderTop: `2px solid ${silo.color}`,
        boxShadow: `0 0 24px ${silo.color}22, inset 0 1px 0 rgba(255,255,255,0.05)`,
        animation: `node-in 0.5s ${index * 0.12}s both`,
      }}
      data-testid={`silo-${index}`}
    >
      <div className="text-center mb-0.5">
        <div className="font-heading text-sm sm:text-base text-white" style={{ color: silo.color }}>
          {silo.team}
        </div>
      </div>
      {silo.tools.map((t) => (
        <div
          key={t.name}
          className="glass rounded-lg px-3 py-2 flex items-center gap-2.5"
          style={{ border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <img src={t.logo} alt={t.name} className="w-5 h-5 rounded shrink-0 bg-white/90 object-contain p-0.5" draggable="false" />
          <span className="font-heading text-[13px] sm:text-sm text-white truncate">{t.name}</span>
        </div>
      ))}
      <div
        className="rounded-lg px-3 py-2 flex items-center gap-2 mt-0.5"
        style={{ background: `${silo.color}1a`, border: `1px solid ${silo.color}55` }}
      >
        <Brain className="w-4 h-4 shrink-0" style={{ color: silo.color }} strokeWidth={1.8} />
        <div className="min-w-0">
          <div className="text-[8px] sm:text-[9px] font-mono uppercase tracking-[0.18em] text-white/50">
            Context trapped in
          </div>
          <div className="font-heading text-[12px] sm:text-sm text-white leading-tight">{silo.memoryIn}</div>
        </div>
      </div>
    </div>
  );
}

/* ───────── 4b) BOTTLENECK (legacy, unused) ───────── */
export function BottleneckSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-4xl w-full mt-2">
        {slide.items.map((p, i) => (
          <div
            key={p.person}
            className="glass rounded-xl p-3 flex flex-col items-center text-center"
            style={{
              borderTop: `1px solid ${p.color}`,
              boxShadow: `0 0 18px ${p.color}33`,
              animation: `node-in 0.5s ${i * 0.08}s both`,
            }}
            data-testid={`person-${i}`}
          >
            <div
              className="w-10 h-10 rounded-full mb-2 flex items-center justify-center font-heading font-medium text-white"
              style={{
                background: `radial-gradient(circle at 35% 30%, ${p.color}99 0%, ${p.color}33 70%, transparent 100%)`,
                boxShadow: `0 0 14px ${p.color}55, inset 0 0 4px rgba(255,255,255,0.3)`,
              }}
            >
              {p.person.split(" ")[1] || p.person[0]}
            </div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-mono text-white/55">
              {p.person}
            </div>
            <div className="font-heading text-sm text-white mt-0.5" style={{ color: p.color }}>
              {p.tool}
            </div>
          </div>
        ))}
      </div>
      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

/* ───────── 6) LAYERED STACK (GrowthOS above tools) ───────── */
export function LayeredStackSlide({ slide }) {
  const layers = [
    { label: "Operator", note: "Sets the goal", color: "#E5E7EB", accent: false },
    { label: "GrowthOS", note: "Operating layer — selects tools, pulls context, executes", color: "#4690D9", accent: true },
    { label: "AI Tools + AI Apps", note: "ChatGPT · Claude · Lovable · Emergent · Gemini · Nano Banana", color: "#9CC6F0", accent: false },
    { label: "The Brain + Brand Memory", note: "Strategic knowledge + your brand's private context", color: "#4690D9", accent: false },
    { label: "E-commerce Platforms", note: "Shopify · Klaviyo · Meta · Google · Omnisend", color: "#34D399", accent: false },
  ];
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="flex flex-col gap-2 w-full max-w-2xl">
        {layers.map((l, i) => (
          <div
            key={l.label}
            className={`relative glass rounded-xl px-4 py-2.5 flex items-center justify-between ${l.accent ? "glass-strong" : ""}`}
            style={{
              borderLeft: `3px solid ${l.color}`,
              boxShadow: l.accent
                ? `0 0 32px ${l.color}55, inset 0 1px 0 rgba(255,255,255,0.08)`
                : `0 0 14px ${l.color}22`,
              animation: `node-in 0.5s ${i * 0.1}s both`,
            }}
          >
            <div>
              <div className="font-heading text-sm sm:text-base tracking-wide" style={{ color: l.accent ? l.color : "#fff" }}>
                {l.label}
              </div>
              <div className="text-[10px] sm:text-xs text-white/55 font-body mt-0.5">{l.note}</div>
            </div>
            {l.accent && (
              <div className="text-[9px] tracking-[0.32em] font-mono uppercase" style={{ color: l.color }}>
                Operating Layer
              </div>
            )}
          </div>
        ))}
      </div>
      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

/* ───────── 7) BRAND AGENT HERO ───────── */
export function BrandAgentHeroSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-5 sm:gap-8 max-w-4xl w-full items-center mt-2">
        <div className="flex justify-center">
          <div className="w-[200px] sm:w-[260px] aspect-square">
            <BrandIntelligenceCore brand={BRANDS[0]} compact />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {slide.capabilities.map((c, i) => (
            <div
              key={c}
              className="glass rounded-lg px-3 py-1.5 flex items-center gap-2"
              style={{ animation: `node-in 0.5s ${i * 0.08}s both` }}
            >
              <Check className="w-3.5 h-3.5 text-[#9CC6F0] shrink-0" strokeWidth={2} />
              <span className="text-[11px] sm:text-sm text-white/85 font-body">{c}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───────── 8) PLATFORMS CONNECTED ───────── */
export function PlatformsConnectedSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="flex flex-wrap items-center justify-center gap-2 max-w-3xl mt-1 mb-4">
        {PLATFORMS.map((p, i) => (
          <div
            key={p.id}
            className="glass-strong rounded-lg pl-2 pr-3 py-1.5 flex items-center gap-2"
            style={{ animation: `node-in 0.5s ${i * 0.06}s both`, boxShadow: "0 0 14px rgba(70,144,217,0.18)" }}
          >
            <img src={p.logo} alt={p.name} className="w-4 h-4 object-contain" draggable="false" />
            <span className="text-[10px] sm:text-xs tracking-[0.16em] uppercase font-mono text-white/85">{p.short}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-3xl w-full">
        {slide.examples.map((ex, i) => (
          <div key={ex} className="glass rounded-md px-2.5 py-1.5 text-[10px] sm:text-xs text-white/75 font-body"
            style={{ animation: `node-in 0.4s ${0.4 + i * 0.05}s both` }}>
            <Sparkles className="inline w-3 h-3 mr-1 text-[#9CC6F0]/80" strokeWidth={1.5} />
            {ex}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── 9) WORKFLOW ───────── */
export function WorkflowSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="glass-strong rounded-xl px-4 py-2 mb-4 max-w-2xl">
        <div className="text-[10px] tracking-[0.32em] uppercase font-mono text-[#9CC6F0]/80 mb-0.5">Example Goal</div>
        <div className="font-heading text-sm sm:text-base text-white italic">{slide.example}</div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-1.5 max-w-5xl">
        {WORKFLOW_STEPS.map((s, i) => (
          <div key={s.step} className="flex items-center gap-1.5">
            <div
              className="glass rounded-md px-2.5 py-1.5 text-center"
              style={{
                borderTop: "1px solid rgba(70,144,217,0.5)",
                boxShadow: "0 0 12px rgba(70,144,217,0.2)",
                animation: `node-in 0.45s ${i * 0.1}s both`,
              }}
            >
              <div className="font-heading text-[11px] sm:text-xs text-white font-medium tracking-wide">{s.step}</div>
              <div className="text-[8px] sm:text-[9px] tracking-[0.16em] uppercase font-mono text-white/45 mt-0.5">{s.note}</div>
            </div>
            {i < WORKFLOW_STEPS.length - 1 && (
              <ChevronRight className="w-3 h-3 text-[#4690D9]/70 shrink-0" strokeWidth={2} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── 10) SKILL LOOP ───────── */
export function SkillLoopSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
        {SKILL_LOOP.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className="glass-strong rounded-full px-3 py-1.5 text-[10px] sm:text-xs tracking-[0.16em] uppercase font-mono text-white/85"
              style={{ animation: `node-in 0.45s ${i * 0.12}s both`, boxShadow: "0 0 14px rgba(70,144,217,0.25)" }}>
              {s}
            </div>
            {i < SKILL_LOOP.length - 1 && <ChevronRight className="w-3 h-3 text-[#4690D9]/70" strokeWidth={2} />}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl w-full">
        {slide.examples.map((e, i) => (
          <div key={e.title} className="glass rounded-lg p-3 text-left"
            style={{ borderLeft: "2px solid rgba(70,144,217,0.5)", animation: `node-in 0.5s ${0.4 + i * 0.1}s both` }}>
            <div className="font-heading text-xs sm:text-sm text-white mb-1">{e.title}</div>
            <div className="text-[10px] sm:text-[11px] text-white/55 font-body leading-relaxed">{e.learns}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── 11) BRAIN HERO ───────── */
export function BrainHeroSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6 relative">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[min(60vh,640px)] aspect-square opacity-90">
          <Brain3D paused={false} />
        </div>
      </div>
      <div className="relative z-10 text-center max-w-2xl">
        <div className="text-[10px] sm:text-[11px] tracking-[0.42em] text-white/45 font-mono uppercase mb-1.5">{slide.eyebrow}</div>
        <h2 className="font-heading text-3xl sm:text-5xl font-light tracking-tight text-white">{slide.title}</h2>
      </div>
    </div>
  );
}

/* ───────── 12) BRAIN vs BRAND MEMORY ───────── */
export function BrainVsMemorySlide({ slide }) {
  const brainItems = ["Courses", "SOPs", "Frameworks", "Playbooks", "E-commerce best practices", "Smart Marketer IP"];
  const memoryItems = ["Product info", "Brand voice", "Customer data", "Past campaigns", "Performance data", "Internal processes"];
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-5 max-w-4xl w-full items-start">
        <Column color="#4690D9" eyebrow="The Brain" title="Shared Strategic Knowledge" items={brainItems} />
        <div className="hidden sm:flex items-center justify-center pt-12">
          <div className="font-heading text-3xl text-white/35">+</div>
        </div>
        <Column color="#9CC6F0" eyebrow="Brand Memory" title="Private Brand Context" items={memoryItems} />
      </div>
      <div className="mt-4 glass-strong rounded-full px-4 py-1.5 text-[11px] tracking-[0.24em] uppercase font-mono text-white/85"
        style={{ boxShadow: "0 0 22px rgba(70,144,217,0.35)" }}>
        = Brand Agent
      </div>
    </div>
  );
}
function Column({ color, eyebrow, title, items }) {
  return (
    <div className="glass rounded-xl p-3 sm:p-4" style={{ borderTop: `2px solid ${color}`, boxShadow: `0 0 18px ${color}33` }}>
      <div className="text-[9px] sm:text-[10px] tracking-[0.32em] uppercase font-mono mb-1" style={{ color }}>{eyebrow}</div>
      <div className="font-heading text-sm sm:text-base text-white mb-2">{title}</div>
      <ul className="space-y-1">
        {items.map((it) => (
          <li key={it} className="flex gap-1.5 items-center text-[11px] sm:text-xs text-white/70">
            <span className="w-1 h-1 rounded-full" style={{ background: color }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ───────── INTELLIGENCE SOURCES (three weighted layers) ───────── */
const SOURCE_ICONS = { database: Database, brain: Brain, globe: Globe };

export function IntelligenceSourcesSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="flex flex-col gap-2.5 sm:gap-3 max-w-2xl w-full">
        {slide.sources.map((s, i) => {
          const Icon = SOURCE_ICONS[s.icon] || Brain;
          return (
            <div
              key={s.key}
              className="glass rounded-xl px-3.5 sm:px-4 py-2.5 sm:py-3"
              data-testid={`intel-source-${s.key}`}
              style={{
                borderLeft: `3px solid ${s.color}`,
                boxShadow: `0 0 18px ${s.color}22`,
                animation: `node-in 0.5s ${i * 0.12}s both`,
              }}
            >
              <div className="flex items-center gap-3">
                {/* rank */}
                <span className="font-heading text-lg sm:text-xl text-white/25 w-4 shrink-0">{i + 1}</span>
                {/* icon */}
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${s.color}1f`, border: `1px solid ${s.color}55` }}
                >
                  <Icon className="w-4.5 h-4.5" style={{ color: s.color, width: 18, height: 18 }} strokeWidth={1.8} />
                </div>
                {/* name + desc */}
                <div className="min-w-0 w-[128px] sm:w-[150px] shrink-0">
                  <div className="font-heading text-sm sm:text-base text-white leading-tight">{s.name}</div>
                  <div className="text-[10px] sm:text-[11px] text-white/50 font-body leading-tight">{s.desc}</div>
                </div>
                {/* items */}
                <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                  {s.items.map((it) => (
                    <span key={it} className="rounded-md px-1.5 py-0.5 text-[9px] sm:text-[10px] font-body text-white/65"
                      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
                      {it}
                    </span>
                  ))}
                </div>
              </div>
              {/* weight bar */}
              <div className="flex items-center gap-2.5 mt-2">
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.07)" }}>
                  <div className="h-full rounded-full" style={{ width: `${s.weight}%`, background: `linear-gradient(90deg, ${s.color}, ${s.color}aa)`, boxShadow: `0 0 10px ${s.color}88` }} />
                </div>
                <span className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.16em] shrink-0" style={{ color: s.color }}>
                  {s.weightLabel}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

/* ───────── 13) GARY ───────── */
export function GarySlide({ slide }) {
  const AGENTS = [15, 38.3, 61.7, 85]; // x positions (%)
  const AGENT_Y = 86;
  const BRAIN = { x: 50, y: 11 };
  const GARY = { x: 50, y: 49 };
  const STAGES = 7; // 0 discover,1 up1,2 generalize,3 up2,4 brainGlow,5 broadcast,6 settle

  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1250);
    return () => clearInterval(id);
  }, []);
  const step = tick % STAGES;
  const activeAgent = Math.floor(tick / STAGES) % AGENTS.length;

  const agentHot = step <= 1; // discovering agent highlighted
  const lineUp1 = step === 1; // agent -> gary
  const garyGen = step === 2;
  const lineUp2 = step === 3; // gary -> brain
  const brainHot = step === 4 || step === 5;
  const broadcast = step === 5; // brain -> all agents
  const allHot = step === 5 || step === 6;
  const tideHot = step === 5 || step === 6;

  const lineCls = (on) =>
    on
      ? "stroke-[#4690D9] [stroke-width:0.5] dash-flow"
      : "stroke-white/12 [stroke-width:0.3]";

  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />

      <div className="relative w-full max-w-3xl" style={{ height: "min(60vh, 500px)" }} data-testid="gary-loop">
        {/* connectors */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          {/* agent -> gary */}
          {AGENTS.map((ax, i) => (
            <line key={`ag-${i}`} x1={ax} y1={AGENT_Y} x2={GARY.x} y2={GARY.y}
              className={lineCls(lineUp1 && i === activeAgent)} strokeDasharray="0.8 1.4" />
          ))}
          {/* gary -> brain */}
          <line x1={GARY.x} y1={GARY.y} x2={BRAIN.x} y2={BRAIN.y}
            className={lineCls(lineUp2)} strokeDasharray="0.8 1.4" />
          {/* brain -> all agents (broadcast) */}
          {AGENTS.map((ax, i) => (
            <line key={`br-${i}`} x1={BRAIN.x} y1={BRAIN.y} x2={ax} y2={AGENT_Y}
              className={broadcast ? "stroke-[#9CC6F0] [stroke-width:0.5] dash-flow" : "stroke-white/0 [stroke-width:0]"}
              strokeDasharray="0.8 1.4" />
          ))}
        </svg>

        {/* The Brain */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: `${BRAIN.x}%`, top: `${BRAIN.y}%` }}>
          <div className="glass-strong rounded-full px-4 py-2 flex items-center gap-2 transition-all duration-500"
            style={{ border: `1px solid ${brainHot ? "#9CC6F0" : "rgba(255,255,255,0.12)"}`,
              boxShadow: brainHot ? "0 0 40px rgba(156,198,240,0.6)" : "0 0 20px rgba(70,144,217,0.2)",
              transform: brainHot ? "scale(1.06)" : "scale(1)" }}>
            <Brain className="w-4 h-4 text-[#9CC6F0]" strokeWidth={1.8} />
            <span className="font-heading text-sm text-white">{slide.tiers.brain}</span>
          </div>
          <span className="text-[9px] font-mono uppercase tracking-[0.2em] transition-opacity duration-300"
            style={{ color: "#9CC6F0", opacity: brainHot ? 1 : 0 }}>+ new skill</span>
        </div>

        {/* Gary */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${GARY.x}%`, top: `${GARY.y}%` }}>
          <div className="relative glass-strong rounded-full pl-1.5 pr-4 py-1.5 flex items-center gap-2.5 transition-all duration-500"
            style={{ boxShadow: garyGen ? "0 0 48px rgba(70,144,217,0.7)" : "0 0 50px rgba(156,198,240,0.2)",
              border: `1px solid ${garyGen ? "#4690D9" : "rgba(255,255,255,0.14)"}`,
              transform: garyGen ? "scale(1.05)" : "scale(1)" }}>
            {garyGen && <span className="absolute inset-0 rounded-full pulse-ring" style={{ boxShadow: "0 0 0 1px rgba(70,144,217,0.5)" }} />}
            <div className="w-11 h-11 rounded-full overflow-hidden ring-2 ring-white/15" style={{ boxShadow: "0 0 16px rgba(70,144,217,0.5)" }}>
              <img src="/img/gary.jpg" alt="Gary" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <div className="text-[9px] tracking-[0.3em] text-white/55 font-mono uppercase">Super Agent</div>
              <div className="font-heading text-xl tracking-wide text-white">Gary</div>
            </div>
          </div>
          <span className="mt-1 text-[9px] font-mono uppercase tracking-[0.18em] transition-opacity duration-300"
            style={{ color: "#4690D9", opacity: garyGen ? 1 : 0 }}>generalizing…</span>
        </div>

        {/* Brand Agents */}
        {AGENTS.map((ax, i) => {
          const isActive = agentHot && i === activeAgent;
          const lit = isActive || allHot;
          return (
            <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: `${ax}%`, top: `${AGENT_Y}%` }}>
              {/* skill tag on the discovering agent */}
              <div className="absolute bottom-full mb-1.5 whitespace-nowrap transition-all duration-300"
                style={{ opacity: isActive ? 1 : 0, transform: isActive ? "translateY(0)" : "translateY(4px)" }}>
                <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-mono text-[#0F2442]"
                  style={{ background: "linear-gradient(135deg,#9CC6F0,#4690D9)", boxShadow: "0 0 14px rgba(70,144,217,0.6)" }}>
                  <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                  {slide.skill}
                </span>
              </div>
              <div className="glass rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 transition-all duration-400"
                style={{ borderTop: `2px solid ${lit ? "#4690D9" : "rgba(156,198,240,0.35)"}`,
                  boxShadow: lit ? "0 0 22px rgba(70,144,217,0.55)" : "0 0 10px rgba(70,144,217,0.12)",
                  transform: lit ? "scale(1.08)" : "scale(1)" }}>
                <Cpu className="w-3.5 h-3.5 text-[#9CC6F0]" strokeWidth={1.8} />
                <span className="font-heading text-[11px] sm:text-xs text-white">Agent</span>
              </div>
              <span className="flex items-center gap-0.5 text-[8px] font-mono uppercase tracking-[0.14em] text-white/40">
                <Lock className="w-2.5 h-2.5" strokeWidth={2} /> IP local
              </span>
            </div>
          );
        })}
      </div>

      {/* Rising-tides caption + privacy note */}
      <div className="mt-1 flex flex-col items-center gap-1">
        <div className="flex items-center gap-2 transition-all duration-500"
          style={{ opacity: tideHot ? 1 : 0.35, transform: tideHot ? "scale(1.03)" : "scale(1)" }}>
          <TrendingUp className="w-4 h-4 text-[#34D399]" strokeWidth={2} />
          <span className="font-heading text-sm sm:text-base text-white italic">{slide.caption}</span>
        </div>
        <span className="text-[10px] sm:text-[11px] font-body text-white/45">{slide.privacyNote}</span>
      </div>
    </div>
  );
}

/* ───────── 14) SUB-AGENT TEAM ───────── */
export function SubAgentTeamSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="glass-strong rounded-xl px-4 py-2 mb-4 max-w-xl">
        <div className="text-[10px] tracking-[0.28em] uppercase font-mono text-[#9CC6F0]/80 mb-0.5">Example Goal</div>
        <div className="font-heading text-sm text-white italic">{slide.example}</div>
      </div>
      <div className="relative w-[min(52vh,460px)] aspect-square max-w-full">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 glass-strong rounded-full px-3 py-1.5 z-10 text-center"
          style={{ boxShadow: "0 0 40px rgba(70,144,217,0.4)" }}>
          <div className="text-[8px] tracking-[0.3em] uppercase font-mono text-[#9CC6F0]/80">Brand Agent</div>
          <div className="font-heading text-sm text-white">Hermes</div>
        </div>
        {slide.items.map((s, i) => {
          const a = (i / slide.items.length) * Math.PI * 2 - Math.PI / 2;
          const x = 50 + Math.cos(a) * 42;
          const y = 50 + Math.sin(a) * 42;
          return (
            <div key={s} className="absolute glass rounded-lg px-2 py-1"
              style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)",
                borderTop: "1px solid rgba(156,198,240,0.5)",
                animation: `node-fade-in 0.5s ${0.2 + i * 0.08}s both`,
                boxShadow: "0 0 14px rgba(156,198,240,0.25)" }}>
              <div className="text-[9px] sm:text-[10px] tracking-[0.14em] uppercase font-mono text-white/85 whitespace-nowrap">{s}</div>
            </div>
          );
        })}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          {slide.items.map((_, i) => {
            const a = (i / slide.items.length) * Math.PI * 2 - Math.PI / 2;
            const x1 = 50 + Math.cos(a) * 8;
            const y1 = 50 + Math.sin(a) * 8;
            const x2 = 50 + Math.cos(a) * 36;
            const y2 = 50 + Math.sin(a) * 36;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(70,144,217,0.35)" strokeWidth="0.2" strokeDasharray="0.6 1.2" />;
          })}
        </svg>
      </div>
    </div>
  );
}

/* ───────── 14) PRIVATE INFRA · SECURITY ───────── */
const SECURITY_ICONS = { shield: Shield, server: Server, lock: Lock, key: KeyRound };

export function PrivateInfraSlide({ slide }) {
  const badges = slide.securityBadges || [];
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />

      {/* Brand environment cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-5xl w-full mt-1">
        {PRIVATE_BRAND_ENVS.map((b, i) => (
          <div key={b.id} className="glass rounded-xl p-3 sm:p-3.5 flex flex-col items-center text-center"
            style={{ borderTop: `2px solid ${b.accent}`, boxShadow: `0 0 22px ${b.accentSoft}`, animation: `node-in 0.5s ${i * 0.12}s both` }}>
            <img src={b.logo} alt={b.short} className="h-6 max-w-[120px] object-contain mb-2" style={{ filter: "brightness(0) invert(1)" }} />
            <div className="text-[9px] tracking-[0.3em] uppercase font-mono mb-2" style={{ color: b.accent }}>Isolated Environment</div>
            <div className="flex flex-col gap-1 w-full">
              {b.blocks.map((blk, j) => {
                const Icon = [Server, Cpu, HardDrive, Lock][j];
                return (
                  <div key={blk} className="glass-strong rounded-md px-2 py-1 flex items-center gap-1.5">
                    <Icon className="w-3 h-3" style={{ color: b.accent }} strokeWidth={1.6} />
                    <span className="text-[10px] sm:text-[11px] tracking-[0.16em] uppercase font-mono text-white/80">{blk}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Security badges row */}
      {badges.length > 0 && (
        <div
          className="mt-4 w-full max-w-5xl grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5"
          data-testid="security-badges"
        >
          {badges.map((bg, i) => {
            const Icon = SECURITY_ICONS[bg.iconId] || Shield;
            const highlight = bg.iconId === "shield";
            return (
              <div
                key={bg.id}
                className="glass-strong rounded-lg px-3 py-2 flex items-start gap-2 relative overflow-hidden"
                data-testid={`security-badge-${bg.id}`}
                style={{
                  borderTop: `1px solid ${highlight ? "rgba(110,231,183,0.55)" : "rgba(70,144,217,0.45)"}`,
                  boxShadow: highlight
                    ? "0 0 24px rgba(16,185,129,0.25), inset 0 1px 0 rgba(255,255,255,0.04)"
                    : "0 0 18px rgba(70,144,217,0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
                  animation: `node-in 0.5s ${0.45 + i * 0.08}s both`,
                }}
              >
                <div
                  className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
                  style={{
                    background: highlight
                      ? "linear-gradient(135deg, rgba(52,211,153,0.28), rgba(16,185,129,0.18))"
                      : "linear-gradient(135deg, rgba(70,144,217,0.28), rgba(0,59,141,0.18))",
                    border: highlight
                      ? "1px solid rgba(110,231,183,0.5)"
                      : "1px solid rgba(70,144,217,0.45)",
                  }}
                >
                  <Icon
                    className="w-3.5 h-3.5"
                    style={{ color: highlight ? "#6EE7B7" : "#9CC6F0" }}
                    strokeWidth={1.8}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-[11px] sm:text-xs text-white tracking-tight leading-snug">
                    {bg.label}
                  </div>
                  <div className="mt-0.5 text-[9px] sm:text-[10px] text-white/55 font-body leading-snug">
                    {bg.note}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-center text-white/55 italic text-[11px] sm:text-xs max-w-3xl px-4">
        We take security seriously — dedicated NVIDIA servers, isolated brand memory, and{" "}
        <span className="text-emerald-300/90 not-italic font-mono tracking-wide">NeMo Guardrails</span>{" "}
        defending every request against prompt injection.
      </p>
    </div>
  );
}

/* ───────── Reusable Lightbox for case-study images ───────── */
function Lightbox({ image, onClose }) {
  useEffect(() => {
    if (!image) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [image, onClose]);

  if (!image) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 sm:px-8 sm:py-12"
      style={{
        background: "rgba(7,20,40,0.88)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        animation: "node-fade-in 0.25s both",
      }}
      onClick={onClose}
      data-testid="lightbox-overlay"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[120] rounded-full w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center transition hover:scale-105"
        aria-label="Close preview"
        data-testid="lightbox-close"
        style={{
          background: "rgba(15,36,66,0.95)",
          border: "1px solid rgba(70,144,217,0.55)",
          boxShadow: "0 0 24px rgba(0,0,0,0.6), 0 0 18px rgba(70,144,217,0.35)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2} />
      </button>

      <div
        className="relative max-w-[92vw] max-h-[88vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt || image.label || ""}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
          style={{
            boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 60px rgba(70,144,217,0.18)",
          }}
          data-testid="lightbox-image"
        />
        {(image.label || image.caption) && (
          <div className="mt-3 text-center text-white/70 font-body text-xs sm:text-sm tracking-wide max-w-2xl">
            {image.label || image.caption}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ───────── 16/17/18) CASE STUDY ───────── */
export function CaseStudySlide({ slide }) {
  const [lightbox, setLightbox] = useState(null);

  // Linked landing-page view (e.g. slide 17 — click opens the real live page)
  if (slide.link && slide.image) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center px-6">
        <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
        <a
          href={slide.link}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative w-full max-w-3xl rounded-2xl overflow-hidden block cursor-pointer"
          data-testid={`case-study-link-${slide.id}`}
          aria-label={slide.linkLabel || "Open the live landing page"}
          style={{
            boxShadow: "0 0 40px rgba(70,144,217,0.2), inset 0 1px 0 rgba(255,255,255,0.06)",
            border: "1px solid rgba(70,144,217,0.35)",
            animation: "node-in 0.6s both",
          }}
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#0B1D38]/95 border-b border-white/10">
            <span className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </span>
            <div className="flex-1 mx-2 sm:mx-3 rounded-md bg-white/[0.06] border border-white/10 px-2.5 sm:px-3 py-1 flex items-center gap-2 min-w-0">
              <Lock className="w-3 h-3 text-[#9CC6F0]/70 shrink-0" strokeWidth={2} />
              <span className="font-mono text-[9px] sm:text-[11px] text-white/60 truncate">
                {slide.linkDomain || slide.link}
              </span>
            </div>
            <span className="hidden sm:flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] tracking-[0.16em] uppercase font-mono text-[#9CC6F0] shrink-0"
              style={{ background: "rgba(70,144,217,0.15)", border: "1px solid rgba(70,144,217,0.4)" }}>
              Live
              <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]" style={{ boxShadow: "0 0 8px #34D399" }} />
            </span>
          </div>

          {/* Screenshot (top-anchored) */}
          <div className="relative w-full aspect-[16/9] overflow-hidden bg-white">
            <img
              src={slide.image}
              alt={slide.imageAlt || slide.title}
              className="absolute top-0 left-0 w-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:-translate-y-[18%]"
              data-testid={`case-study-image-${slide.id}`}
              draggable="false"
            />
            {/* Hover CTA overlay */}
            <div className="absolute inset-0 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: "linear-gradient(to top, rgba(11,29,56,0.55), transparent 55%)" }}>
              <span className="flex items-center gap-2 rounded-full px-4 py-2 text-[11px] sm:text-xs tracking-[0.14em] uppercase font-mono text-white"
                style={{ background: "rgba(15,36,66,0.95)", border: "1px solid rgba(70,144,217,0.55)", boxShadow: "0 0 22px rgba(70,144,217,0.45)" }}>
                {slide.linkLabel || "Open the live landing page"}
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
              </span>
            </div>
          </div>
        </a>
        <p className="mt-3 text-center text-white/55 italic text-[11px] sm:text-sm max-w-2xl px-4">
          {slide.quote}
        </p>
      </div>
    );
  }

  // Triptych view (e.g. slide 17 — three screenshots telling a sequence)
  if (slide.images && slide.images.length > 0) {
    const cols = slide.images.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
    const maxW = slide.images.length >= 3 ? "max-w-5xl" : "max-w-4xl";
    const aspect = slide.images.length >= 3 ? "aspect-[4/3]" : "aspect-[3/2]";
    return (
      <div className="h-full w-full flex flex-col items-center justify-center px-6">
        <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
        <div className={`w-full ${maxW} grid grid-cols-1 ${cols} gap-3 sm:gap-4`}>
          {slide.images.map((img, i) => (
            <div
              key={i}
              className="glass-strong rounded-xl relative overflow-hidden flex flex-col"
              data-testid={`case-study-image-${slide.id}-${i}`}
              style={{
                boxShadow: "0 0 28px rgba(70,144,217,0.18), inset 0 1px 0 rgba(255,255,255,0.05)",
                animation: `node-in 0.5s ${i * 0.12}s both`,
              }}
            >
              <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
              <button
                type="button"
                onClick={() => setLightbox(img)}
                className={`relative ${aspect} w-full flex items-center justify-center overflow-hidden bg-white/[0.03] group cursor-zoom-in`}
                aria-label={`Enlarge: ${img.label || img.alt || "screenshot"}`}
                data-testid={`case-study-image-button-${slide.id}-${i}`}
              >
                <img
                  src={img.src}
                  alt={img.alt || img.label}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                  style={{ filter: "drop-shadow(0 8px 22px rgba(0,0,0,0.4))" }}
                />
                <span className="absolute top-2 right-2 glass rounded-full w-7 h-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <ZoomIn className="w-3.5 h-3.5 text-white/90" strokeWidth={1.8} />
                </span>
              </button>
              <div className="relative px-3 py-2 border-t border-white/10 flex items-center gap-2">
                <span
                  className="font-mono text-[9px] sm:text-[10px] tracking-[0.18em] uppercase shrink-0"
                  style={{ color: "rgba(70,144,217,0.85)" }}
                >
                  0{i + 1}
                </span>
                <span className="font-body text-[10px] sm:text-[11px] text-white/75 leading-tight">
                  {img.label}
                </span>
              </div>
            </div>
          ))}
        </div>
        <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
      </div>
    );
  }

  // Single image view (e.g. slide 16)
  const singleImg = slide.image
    ? { src: slide.image, alt: slide.imageAlt || slide.title, label: slide.imageAlt || slide.title }
    : null;

  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="glass-strong rounded-2xl w-full max-w-3xl aspect-[16/9] flex items-center justify-center relative overflow-hidden"
        style={{ boxShadow: "0 0 40px rgba(70,144,217,0.18), inset 0 1px 0 rgba(255,255,255,0.05)" }}>
        <div className="absolute inset-0 grid-bg opacity-50" />
        {singleImg ? (
          <button
            type="button"
            onClick={() => setLightbox(singleImg)}
            className="relative w-full h-full flex items-center justify-center group cursor-zoom-in"
            aria-label={`Enlarge: ${singleImg.alt}`}
            data-testid={`case-study-image-button-${slide.id}`}
          >
            <img
              src={singleImg.src}
              alt={singleImg.alt}
              className="relative w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
              data-testid={`case-study-image-${slide.id}`}
              style={{
                filter: "drop-shadow(0 12px 32px rgba(0,0,0,0.45))",
              }}
            />
            <span className="absolute top-3 right-3 glass rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              <ZoomIn className="w-4 h-4 text-white/90" strokeWidth={1.8} />
            </span>
          </button>
        ) : (
          <div className="relative text-center p-6">
            <div className="text-[9px] sm:text-[10px] tracking-[0.4em] uppercase font-mono text-[#9CC6F0]/70 mb-2">
              Screenshot Placeholder
            </div>
            <div className="font-heading text-base sm:text-lg text-white/85 max-w-md mx-auto">
              {slide.placeholder}
            </div>
            <div className="mt-3 text-[10px] tracking-[0.24em] uppercase font-mono text-white/35">
              Replace with actual screenshots
            </div>
          </div>
        )}
      </div>
      <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

/* ───────── 18) AD CREATIVE (funnel-aware creative library) ───────── */
export function AdCreativeSlide({ slide }) {
  const [lightbox, setLightbox] = useState(null);
  const img = { src: slide.image, alt: slide.imageAlt || slide.title, label: slide.imageAlt };

  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-4 sm:gap-6 max-w-5xl w-full items-center">
        {/* Creative library image */}
        <button
          type="button"
          onClick={() => setLightbox(img)}
          className="group relative rounded-xl overflow-hidden glass-strong cursor-zoom-in"
          aria-label="Enlarge the ad creative library"
          data-testid={`ad-creative-image-button-${slide.id}`}
          style={{
            boxShadow: "0 0 32px rgba(70,144,217,0.2), inset 0 1px 0 rgba(255,255,255,0.05)",
            border: "1px solid rgba(70,144,217,0.3)",
            animation: "node-in 0.6s both",
          }}
        >
          <img
            src={slide.image}
            alt={img.alt}
            className="w-full max-h-[62vh] object-contain transition-transform duration-500 group-hover:scale-[1.02]"
            data-testid={`ad-creative-image-${slide.id}`}
            draggable="false"
          />
          <span className="absolute top-2 right-2 glass rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <ZoomIn className="w-4 h-4 text-white/90" strokeWidth={1.8} />
          </span>
        </button>

        {/* Right rail: signals + funnel */}
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase font-mono text-white/45 mb-1.5">
              Signals it reads
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-1.5">
              {slide.signals.map((s, i) => (
                <div
                  key={s.label}
                  className="glass rounded-md px-2.5 py-1.5 flex items-start gap-1.5"
                  style={{ borderLeft: "2px solid rgba(70,144,217,0.55)", animation: `node-in 0.45s ${0.2 + i * 0.07}s both` }}
                  data-testid={`ad-creative-signal-${i}`}
                >
                  <BarChart3 className="w-3 h-3 text-[#9CC6F0] shrink-0 mt-0.5" strokeWidth={1.8} />
                  <div className="min-w-0">
                    <div className="font-heading text-[11px] sm:text-xs text-white leading-tight">{s.label}</div>
                    <div className="text-[9px] sm:text-[10px] text-white/50 font-body leading-tight">{s.note}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase font-mono text-white/45 mb-1.5">
              Built for every funnel stage
            </div>
            <div className="flex flex-col gap-1.5">
              {slide.funnel.map((f, i) => (
                <div
                  key={f.stage}
                  className="glass rounded-md px-2.5 py-1.5 flex items-center gap-2"
                  style={{ borderTop: `1px solid ${f.color}66`, boxShadow: `0 0 12px ${f.color}22`, animation: `node-in 0.45s ${0.45 + i * 0.08}s both` }}
                  data-testid={`ad-creative-funnel-${f.stage.toLowerCase()}`}
                >
                  <span
                    className="shrink-0 font-mono text-[10px] tracking-[0.14em] rounded px-1.5 py-0.5"
                    style={{ color: f.color, background: `${f.color}1f`, border: `1px solid ${f.color}55` }}
                  >
                    {f.stage}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-[11px] sm:text-xs text-white leading-tight">{f.name}</div>
                    <div className="text-[9px] sm:text-[10px] text-white/50 font-body leading-tight">{f.note}</div>
                  </div>
                  <span className="shrink-0 font-mono text-[9px] text-white/40">{f.tiles}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

/* ───────── 19) BENEFITS ───────── */
export function BenefitsSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-3xl w-full">
        {BUSINESS_BENEFITS.map((b, i) => (
          <div key={b} className="glass rounded-lg px-3 py-2 flex items-center gap-2"
            style={{ borderLeft: "2px solid rgba(70,144,217,0.55)", animation: `node-in 0.45s ${i * 0.06}s both` }}>
            <Check className="w-3.5 h-3.5 text-[#9CC6F0] shrink-0" strokeWidth={2} />
            <span className="text-[11px] sm:text-sm text-white/85 font-body">{b}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── 20) CHECKLIST (Client Onboarding) ───────── */
export function ChecklistSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <div className="text-center mb-5 sm:mb-7 max-w-3xl mx-auto">
        <div className="text-[10px] sm:text-[11px] tracking-[0.5em] text-white/45 font-mono uppercase mb-2">
          {slide.eyebrow}
        </div>
        <h2 className="shimmer font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
          {slide.title}
        </h2>
      </div>

      <div className="w-full max-w-3xl space-y-3 sm:space-y-3.5">
        {slide.items.map((item, i) => (
          <div
            key={i}
            className="glass-strong rounded-xl px-4 sm:px-5 py-3.5 sm:py-4 flex items-start gap-3 sm:gap-4 relative overflow-hidden"
            data-testid={`checklist-item-${i}`}
            style={{
              borderLeft: "2px solid rgba(70,144,217,0.55)",
              boxShadow: "0 0 24px rgba(70,144,217,0.15), inset 0 1px 0 rgba(255,255,255,0.05)",
              animation: `node-in 0.5s ${i * 0.1}s both`,
            }}
          >
            <div
              className="shrink-0 mt-0.5 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-mono text-[11px] sm:text-xs tracking-[0.1em]"
              style={{
                background: "linear-gradient(135deg, rgba(70,144,217,0.25), rgba(0,59,141,0.18))",
                border: "1px solid rgba(70,144,217,0.45)",
                color: "#CFE3FB",
                boxShadow: "0 0 14px rgba(70,144,217,0.35)",
              }}
            >
              0{i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm sm:text-base lg:text-lg text-white tracking-tight leading-snug">
                {item.title}
              </div>
              {item.note && (
                <div className="mt-1 text-[11px] sm:text-xs text-white/55 font-body leading-relaxed">
                  {item.note}
                </div>
              )}
            </div>
            <Check className="shrink-0 w-4 h-4 sm:w-5 sm:h-5 text-[#9CC6F0]/80 mt-1.5" strokeWidth={2} />
          </div>
        ))}
      </div>

      {slide.body && slide.body.length > 0 && (
        <div className="mt-5 sm:mt-6 max-w-2xl text-center">
          {slide.body.map((line, i) => (
            <p key={i} className="text-white/55 italic text-xs sm:text-sm font-body">
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────── 21) PRICING ───────── */
const PRICING_ICONS = { agent: Cpu, team: Users, alloy: TrendingDown, brain: Brain, owned: Package, training: Video };

export function PricingSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />

      <div className="w-full max-w-4xl mx-auto flex flex-col gap-3.5 sm:gap-4">
        {/* One-time setup fee — full-width banner on top */}
        {slide.setupFee && (
          <div
            className="glass-strong rounded-2xl w-full px-6 py-3.5 flex items-center justify-center gap-4"
            data-testid="pricing-setup-fee"
            style={{
              borderTop: "2px solid rgba(70,144,217,0.5)",
              boxShadow: "0 0 34px rgba(70,144,217,0.28), inset 0 1px 0 rgba(255,255,255,0.06)",
              animation: "node-in 0.5s both",
            }}
          >
            <span className="text-[11px] sm:text-sm tracking-[0.3em] uppercase font-mono text-white/60">
              One-time setup fee
            </span>
            <span className="font-heading text-3xl sm:text-4xl font-light text-white leading-none">
              {slide.setupFee}
            </span>
          </div>
        )}

        {/* New pricing — two plans side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {slide.plans.map((p, i) => (
            <div
              key={p.id}
              className={`relative glass rounded-2xl px-6 py-5 flex flex-col items-center text-center ${p.featured ? "glass-strong" : ""}`}
              data-testid={`pricing-plan-${p.id}`}
              style={{
                borderTop: `2px solid ${p.featured ? "#4690D9" : "rgba(255,255,255,0.15)"}`,
                boxShadow: p.featured
                  ? "0 0 40px rgba(70,144,217,0.4), inset 0 1px 0 rgba(255,255,255,0.06)"
                  : "0 0 18px rgba(70,144,217,0.14)",
                animation: `node-in 0.5s ${i * 0.1}s both`,
              }}
            >
              {p.badge && (
                <span
                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-[9px] tracking-[0.22em] uppercase font-mono text-[#0F2442] whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg,#9CC6F0,#4690D9)", boxShadow: "0 0 16px rgba(70,144,217,0.6)" }}
                >
                  {p.badge}
                </span>
              )}
              <div className="text-[11px] tracking-[0.28em] uppercase font-mono text-white/55 mb-2">{p.name}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-4xl sm:text-5xl font-light text-white leading-none">{p.price}</span>
                <span className="text-sm text-white/55 font-body">{p.cadence}</span>
              </div>
              <div className="text-xs sm:text-sm text-white/50 font-body mt-1.5">{p.note}</div>
            </div>
          ))}
        </div>

        {/* Usage note */}
        <div className="flex items-center justify-center gap-2 text-center" data-testid="pricing-usage-note">
          <TrendingDown className="w-4 h-4 text-[#34D399] shrink-0" strokeWidth={1.8} />
          <span className="text-xs sm:text-sm text-white/65 font-body">{slide.usageNote}</span>
        </div>

        {/* Value stack — what they get */}
        <div>
          <div className="text-center text-[11px] tracking-[0.34em] uppercase font-mono text-white/45 mb-2.5">
            Everything included
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {slide.valueStack.map((v, i) => {
              const Icon = PRICING_ICONS[v.iconId] || Check;
              const spanFull = i === slide.valueStack.length - 1 && slide.valueStack.length % 2 === 1;
              return (
                <div
                  key={v.title}
                  className={`glass rounded-xl px-4 py-2.5 flex items-start gap-3 ${spanFull ? "sm:col-span-2 sm:justify-center" : ""}`}
                  data-testid={`pricing-value-${v.iconId}`}
                  style={{ borderLeft: "2px solid rgba(70,144,217,0.55)", animation: `node-in 0.45s ${0.25 + i * 0.08}s both` }}
                >
                  <div
                    className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
                    style={{
                      background: "linear-gradient(135deg, rgba(70,144,217,0.28), rgba(0,59,141,0.18))",
                      border: "1px solid rgba(70,144,217,0.45)",
                    }}
                  >
                    <Icon className="w-4 h-4 text-[#9CC6F0]" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading text-sm sm:text-base text-white leading-tight">{v.title}</div>
                    <div className="text-[11px] sm:text-xs text-white/55 font-body leading-snug mt-0.5">{v.note}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── (legacy) CLOSING ───────── */
export function ClosingSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6 text-center">
      <div className="text-[10px] sm:text-[11px] tracking-[0.5em] text-white/45 font-mono uppercase mb-3">{slide.eyebrow}</div>
      <h2 className="shimmer font-heading text-3xl sm:text-5xl lg:text-6xl font-light tracking-tight max-w-3xl px-4 mb-5">
        {slide.title}
      </h2>
      <div className="space-y-2 max-w-2xl mb-6">
        {slide.body.map((line, i) => (
          <p key={i} className="text-white/65 text-sm sm:text-base font-body">{line}</p>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
        {STATS.map((s, i) => (
          <div key={i} className="text-center px-3">
            <div className="font-heading text-xl sm:text-3xl font-light"
              style={{ background: "linear-gradient(135deg,#fff 0%,#9CC6F0 60%,#9CC6F0 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
              {s.value}
            </div>
            <div className="text-[8px] sm:text-[9px] tracking-[0.28em] uppercase font-mono text-white/45">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {slide.ctas.map((c, i) => (
          <button key={c} type="button"
            className="glass-strong rounded-full px-4 py-2 text-[11px] sm:text-xs tracking-[0.2em] uppercase font-mono text-white hover:bg-white/10 transition"
            data-testid={`cta-${i}`}
            style={{ boxShadow: "0 0 22px rgba(70,144,217,0.3)" }}>
            {c}  →
          </button>
        ))}
      </div>
    </div>
  );
}

/* ───────── 13) HUB WALKTHROUGH (Slack video + clickable GrowthOS HUB) ───────── */
export function HubWalkthroughSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-7 max-w-6xl w-full items-stretch">
        {/* LEFT — Slack conversation video */}
        <div className="flex flex-col">
          <div
            className="rounded-xl overflow-hidden glass"
            data-testid="hub-slack-video"
            style={{
              border: "1px solid rgba(70,144,217,0.3)",
              boxShadow: "0 0 32px rgba(70,144,217,0.2), inset 0 1px 0 rgba(255,255,255,0.05)",
              animation: "node-in 0.6s both",
            }}
          >
            {/* window chrome */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#0B1D38]/95 border-b border-white/10">
              <span className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </span>
              <span className="flex-1 text-center font-mono text-[10px] sm:text-[11px] text-white/55 truncate">
                Slack · Young Again
              </span>
              <span className="w-10" />
            </div>
            <div className="relative w-full bg-white" style={{ aspectRatio: "1280 / 820" }}>
              <video
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hub-slack-video-el"
              >
                {slide.videoWebm && <source src={slide.videoWebm} type="video/webm" />}
                <source src={slide.video} type="video/mp4" />
              </video>
            </div>
          </div>
          <div className="mt-2.5 flex items-center justify-center gap-2 text-white/60">
            <MessageSquare className="w-3.5 h-3.5 text-[#9CC6F0]" strokeWidth={1.8} />
            <span className="text-[11px] sm:text-sm font-body">{slide.slackCaption}</span>
          </div>
        </div>

        {/* RIGHT — clickable GrowthOS HUB */}
        <div className="flex flex-col">
          <a
            href={slide.hubLink}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative rounded-xl overflow-hidden block cursor-pointer"
            data-testid="hub-link"
            aria-label={slide.hubCaption}
            style={{
              border: "1px solid rgba(70,144,217,0.35)",
              boxShadow: "0 0 32px rgba(70,144,217,0.22), inset 0 1px 0 rgba(255,255,255,0.06)",
              animation: "node-in 0.6s 0.1s both",
            }}
          >
            {/* browser chrome */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#0B1D38]/95 border-b border-white/10">
              <span className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
              </span>
              <div className="flex-1 mx-2 rounded-md bg-white/[0.06] border border-white/10 px-2.5 py-1 flex items-center gap-2 min-w-0">
                <Lock className="w-3 h-3 text-[#9CC6F0]/70 shrink-0" strokeWidth={2} />
                <span className="font-mono text-[9px] sm:text-[11px] text-white/60 truncate">
                  {slide.hubLinkDomain}
                </span>
              </div>
              <span
                className="hidden sm:flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] tracking-[0.16em] uppercase font-mono text-[#9CC6F0] shrink-0"
                style={{ background: "rgba(70,144,217,0.15)", border: "1px solid rgba(70,144,217,0.4)" }}
              >
                Live
                <span className="w-1.5 h-1.5 rounded-full bg-[#34D399]" style={{ boxShadow: "0 0 8px #34D399" }} />
              </span>
            </div>
            {/* screenshot */}
            <div className="relative w-full bg-white overflow-hidden" style={{ aspectRatio: "1280 / 820" }}>
              <img
                src={slide.hubImage}
                alt={slide.hubImageAlt}
                className="absolute top-0 left-0 w-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:-translate-y-[22%]"
                draggable="false"
              />
              <div
                className="absolute inset-0 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: "linear-gradient(to top, rgba(11,29,56,0.55), transparent 55%)" }}
              >
                <span
                  className="flex items-center gap-2 rounded-full px-4 py-2 text-[11px] sm:text-xs tracking-[0.14em] uppercase font-mono text-white"
                  style={{ background: "rgba(15,36,66,0.95)", border: "1px solid rgba(70,144,217,0.55)", boxShadow: "0 0 22px rgba(70,144,217,0.45)" }}
                >
                  Open the HUB
                  <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                </span>
              </div>
            </div>
          </a>
          <div className="mt-2.5 flex items-center justify-center gap-2 text-white/60">
            <LayoutDashboard className="w-3.5 h-3.5 text-[#9CC6F0]" strokeWidth={1.8} />
            <span className="text-[11px] sm:text-sm font-body">{slide.hubCaption}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── IP VALUE (Smart Marketer course library the agent is trained on) ───────── */
const IP_CAT_COLOR = { course: "#4690D9", blueprint: "#9CC6F0", mini: "#34D399" };

export function IpValueSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mb-3">
        {Object.entries(slide.categories).map(([key, label]) => (
          <span key={key} className="flex items-center gap-1.5" data-testid={`ip-legend-${key}`}>
            <span className="w-2 h-2 rounded-full" style={{ background: IP_CAT_COLOR[key], boxShadow: `0 0 8px ${IP_CAT_COLOR[key]}` }} />
            <span className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.2em] text-white/55">{label}</span>
          </span>
        ))}
      </div>

      {/* Course grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2.5 max-w-5xl w-full">
        {slide.courses.map((c, i) => (
          <div
            key={c.name}
            className="glass rounded-lg px-3 py-2 flex items-center justify-between gap-2"
            data-testid={`ip-course-${i}`}
            style={{ borderLeft: `2px solid ${IP_CAT_COLOR[c.cat]}`, animation: `node-in 0.4s ${i * 0.03}s both` }}
          >
            <span className="font-heading text-[12px] sm:text-sm text-white truncate">{c.name}</span>
            <span className="font-mono text-[12px] sm:text-sm text-[#9CC6F0] shrink-0">{c.price}</span>
          </div>
        ))}
      </div>

      {/* Total */}
      <div
        className="glass-strong rounded-2xl mt-4 px-6 sm:px-8 py-3.5 flex items-center justify-center gap-4 sm:gap-6 max-w-5xl w-full"
        data-testid="ip-total"
        style={{
          borderTop: "2px solid #4690D9",
          boxShadow: "0 0 36px rgba(70,144,217,0.3), inset 0 1px 0 rgba(255,255,255,0.06)",
          animation: "node-in 0.5s 0.5s both",
        }}
      >
        <div className="text-left">
          <div className="text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.28em] text-white/55">
            {slide.totalLabel}
          </div>
          <div className="text-[11px] sm:text-sm text-white/60 font-body">{slide.totalNote}</div>
        </div>
        <div className="font-heading text-3xl sm:text-5xl font-light text-white leading-none whitespace-nowrap">
          {slide.total}
        </div>
      </div>
    </div>
  );
}

/* ───────── 14) VIDEO SLIDE (full-screen autoplay demo) ───────── */
export function VideoSlide({ slide }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    try {
      v.muted = true;
      v.currentTime = 0;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {}
  }, []);

  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />
      <div
        className="rounded-xl overflow-hidden glass w-full"
        data-testid="video-slide"
        style={{
          maxWidth: "min(92vw, 1120px)",
          border: "1px solid rgba(70,144,217,0.3)",
          boxShadow: "0 0 40px rgba(70,144,217,0.22), inset 0 1px 0 rgba(255,255,255,0.05)",
          animation: "node-in 0.6s both",
        }}
      >
        <div className="relative w-full bg-black" style={{ aspectRatio: slide.aspect || "16 / 9", maxHeight: "66vh" }}>
          <video
            ref={ref}
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="auto"
            className="absolute inset-0 w-full h-full object-contain bg-black"
            data-testid="video-slide-el"
          >
            {slide.videoWebm && <source src={slide.videoWebm} type="video/webm" />}
            <source src={slide.video} type="video/mp4" />
          </video>
        </div>
      </div>
    </div>
  );
}

/* ───────── 15) CHANNELS (No dashboards · talk to the agent where you work) ───────── */
export function ChannelsSlide({ slide }) {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center px-6">
      <SlideHeader eyebrow={slide.eyebrow} title={slide.title} />

      {/* Chat mockup */}
      <div
        className="glass-strong rounded-2xl w-full max-w-2xl px-4 sm:px-6 py-4 sm:py-5 relative overflow-hidden"
        style={{
          boxShadow: "0 0 38px rgba(70,144,217,0.22), inset 0 1px 0 rgba(255,255,255,0.05)",
          animation: "node-in 0.6s 0.05s both",
        }}
        data-testid="channels-chat-mockup"
      >
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

        {/* User message */}
        <div className="relative flex items-start gap-2.5 mb-3">
          <div
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-mono text-[10px] text-white/85"
            style={{
              background: "linear-gradient(135deg, rgba(70,144,217,0.35), rgba(156,198,240,0.18))",
              border: "1px solid rgba(70,144,217,0.4)",
            }}
          >
            DM
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-0.5">
              <span className="font-heading text-[12px] sm:text-sm text-white/90">Devyn</span>
              <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-white/35">9:41 AM</span>
            </div>
            <div className="text-[12px] sm:text-sm text-white/80 font-body leading-relaxed">
              <span className="text-[#9CC6F0]">@Agent</span>{" "}
              build a 4-email Memorial Day flow for engaged shoppers — 20% off, May 21–27.
            </div>
          </div>
        </div>

        {/* Agent response */}
        <div className="relative flex items-start gap-2.5">
          <div
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(70,144,217,0.45), rgba(0,59,141,0.25))",
              border: "1px solid rgba(70,144,217,0.5)",
              boxShadow: "0 0 14px rgba(70,144,217,0.45)",
            }}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#CFE3FB]" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 mb-0.5">
              <span className="font-heading text-[12px] sm:text-sm text-white/90">Brand Agent</span>
              <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-[#9CC6F0]/70">Online</span>
            </div>
            <div className="text-[12px] sm:text-sm text-white/80 font-body leading-relaxed">
              On it. Pulling segmentation, drafting copy, designing the template — I&apos;ll post the review doc in this thread shortly.
            </div>
          </div>
        </div>
      </div>

      {/* Channel chips */}
      <div className="mt-5 sm:mt-6 w-full max-w-3xl">
        <div className="text-center text-[9px] sm:text-[10px] tracking-[0.42em] uppercase font-mono text-white/40 mb-3">
          Available in the channels your team already uses
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {slide.items.map((c, i) => (
            <div
              key={c.id}
              className="glass rounded-full pl-2 pr-3.5 py-1.5 flex items-center gap-2"
              data-testid={`channel-${c.id}`}
              style={{
                borderTop: `1px solid ${c.accent}55`,
                boxShadow: `0 0 14px ${c.accent}22`,
                animation: `node-in 0.45s ${0.15 + i * 0.08}s both`,
              }}
            >
              <img
                src={c.logo}
                alt={c.name}
                className="w-4 h-4 sm:w-4.5 sm:h-4.5 object-contain"
                style={{ filter: "drop-shadow(0 0 4px rgba(255,255,255,0.15))" }}
                draggable={false}
              />
              <span className="text-[10px] sm:text-xs tracking-[0.14em] uppercase font-mono text-white/85 whitespace-nowrap">
                {c.short || c.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {slide.footnote && <Footnote text={slide.footnote} />}
    </div>
  );
}

/* ───────── DISPATCHER ───────── */
export default function SlideRenderer({ slide }) {
  const map = {
    title: TitleSlide,
    chipgrid: ChipGridSlide,
    fragmentation: FragmentationSlide,
    bottleneck: BottleneckSlide,
    silos: SilosSlide,
    layeredstack: LayeredStackSlide,
    "brand-agent-hero": BrandAgentHeroSlide,
    "platforms-connected": PlatformsConnectedSlide,
    workflow: WorkflowSlide,
    "skill-loop": SkillLoopSlide,
    brain: BrainHeroSlide,
    "brain-vs-memory": BrainVsMemorySlide,
    "intelligence-sources": IntelligenceSourcesSlide,
    gary: GarySlide,
    "subagent-team": SubAgentTeamSlide,
    "private-infra": PrivateInfraSlide,
    channels: ChannelsSlide,
    "hub-walkthrough": HubWalkthroughSlide,
    "video-slide": VideoSlide,
    "ip-value": IpValueSlide,
    "case-study": CaseStudySlide,
    "ad-creative": AdCreativeSlide,
    benefits: BenefitsSlide,
    pricing: PricingSlide,
    checklist: ChecklistSlide,
    closing: ClosingSlide,
  };
  const Comp = map[slide.layout] || TitleSlide;
  return <Comp slide={slide} />;
}
